import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSeo } from "../../hooks/useSeo";
import { COMMENTS, COLUMNS } from "../../mocks/mockContent";
import Reveal from "../../components/feature/Reveal";

const PRINCIPLES = [
  { icon: "ri-checkbox-circle-line", title: "先核实，再发布", desc: "每条内容都尽量追溯一手信源与权威媒体，来路不明的传闻会明确标注「存疑」，绝不拿未经证实的消息博流量。" },
  { icon: "ri-empathize-line", title: "理性克制，不煽动", desc: "反对借热点制造对立、贩卖焦虑。我们把情绪留给当事人，把判断交给读者。" },
  { icon: "ri-shield-check-line", title: "尊重事实与版权", desc: "不搬运盗版资源，不泄露隐私，转载图文注明出处。健康吃瓜，从尊重开始。" },
  { icon: "ri-community-line", title: "真诚陪伴，长效更新", desc: "内容不必惊天动地，贵在日拱一卒。我们用稳定的更新，陪伴每一位瓜友认真生活。" },
];

const STATS = [
  { icon: "ri-sun-line", num: "4", label: "常设栏目" },
  { icon: "ri-time-line", num: "日更", label: "稳定输出" },
  { icon: "ri-user-smile-line", num: "正向", label: "内容基调" },
  { icon: "ri-map-pin-line", num: "全天候", label: "热点追踪" },
];

const SUBMIT_ADDR = "https://readdy.ai/api/form/dafbq6htto0ceqqnh9p0";

const FORM_ID = "dafbq6htto0ceqqnh9p0";

export default function AboutPage() {
  const { hash } = useLocation();
  const [status, setStatus] = useState<"idle" | "ok" | "err">("idle");
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (hash) {
      const t = setTimeout(() => {
        const el = document.getElementById(hash.slice(1));
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 120);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [hash]);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const hp = ((fd.get("contact_alt") as string) || "").trim();
    // silently swallow bots: treat like a normal success, send nothing
    if (hp) {
      setStatus("ok");
      setFormError("");
      return;
    }
    const payload = new URLSearchParams();
    fd.forEach((value, key) => {
      if (key === "contact_alt") return;
      if (typeof value === "string" && value.trim()) payload.append(key, value.trim());
    });

    try {
      const res = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const msg =
        parsed?.meta?.message ||
        parsed?.meta?.detail ||
        parsed?.message ||
        "";
      const spamHit = responseText.toLowerCase().includes("spam") || msg.toLowerCase().includes("spam");
      if (res.ok && parsed?.code === "OK" && !spamHit) {
        setStatus("ok");
        setFormError("");
        form.reset();
      } else {
        setStatus("err");
        setFormError(spamHit ? "提交内容疑似包含垃圾信息，请核对后重试。" : msg || "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("err");
      setFormError("网络异常，请稍后再试。");
    }
  };

  const columns = COLUMNS.filter((c) => c.key !== "all");

  useSeo({
    title: "关于我们 · 瓜田日报 — 品牌理念、内容原则与投稿联系",
    description:
      "了解瓜田日报：健康吃瓜资讯门户的定位与理念、坚持先核实再发布的四项内容原则、瓜友口碑，以及投稿与合作联系方式。",
    path: "/about",
    type: "website",
    ld: {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "关于瓜田日报",
      inLanguage: "zh-CN",
      about: "瓜田日报是一站式健康吃瓜资讯门户，聚合明星动态、影视综艺、网络热梗与热搜解读。",
    },
  });

  return (
    <>
      {/* intro */}
      <section className="relative overflow-hidden bg-background-100/60">
        <div
          className="pointer-events-none absolute inset-0"
          style={{ background: "radial-gradient(900px 420px at 90% -10%, oklch(var(--accent-100)/0.7), transparent 55%)" }}
        ></div>
        <div className="relative mx-auto max-w-6xl px-4 py-10 md:px-6 md:py-14">
          <nav aria-label="面包屑" className="text-sm text-foreground-500">
            <ol className="flex items-center gap-2">
              <li><Link to="/" className="hover:text-primary-700">首页</Link></li>
              <li aria-hidden="true"><i className="ri-arrow-right-s-line"></i></li>
              <li aria-current="page" className="font-medium text-foreground-800">关于我们</li>
            </ol>
          </nav>
          <div className="mt-6 max-w-3xl">
            <p className="flex items-center gap-2 text-sm font-semibold tracking-wide text-primary-700">
              <i className="ri-flag-2-line" aria-hidden="true"></i>
              关于瓜田日报
            </p>
            <h1 className="mt-3 font-display text-3xl font-bold leading-snug text-foreground-950 md:text-4xl">
              健康吃瓜，理性围观。<br className="hidden sm:block" /> 我们想做有温度的资讯入口
            </h1>
          </div>
        </div>
      </section>

      {/* who we are */}
      <section className="bg-background-50" aria-label="平台介绍">
        <div className="mx-auto grid max-w-6xl gap-10 px-4 py-12 md:px-6 md:py-16 lg:grid-cols-2">
          <Reveal>
            <h2 className="font-display text-2xl font-bold text-foreground-950 md:text-3xl">我们是谁</h2>
            <p className="mt-5 text-[15px] leading-8 text-foreground-700">
              瓜田日报是一个面向普通网友的简体中文娱乐吃瓜资讯门户。我们不追速度第一，也不靠夸张标题抢眼球，
              而是把散落在各处的明星动态、影视综艺、网络热梗与热搜话题，整理成清晰、可读、有据可查的图文内容。
            </p>
            <p className="mt-4 text-[15px] leading-8 text-foreground-700">
              在这个信息高度碎片化的时代，「吃瓜」早已不只是消遣，它是一扇观察公共情绪与流行文化的窗口。
              我们希望帮每一位读者省下筛选的时间，同时守住一份难得的清醒与善意——热闹要追，底线更要守。
            </p>
            <p className="mt-4 text-[15px] leading-8 text-foreground-700">
              如果你也认同「理性吃瓜」这件事，欢迎常来坐坐，也欢迎把你在追的新瓜分享给我们。
            </p>
          </Reveal>

          <Reveal delay={100}>
            <div className="rounded-2xl border hairline bg-background-100/50 p-6">
              <h3 className="font-display text-lg font-bold text-foreground-950">平台速览</h3>
              <dl className="mt-5 grid grid-cols-2 gap-4">
                {STATS.map((s) => (
                  <div key={s.label} className="rounded-xl bg-background-50 p-4">
                    <dd className="flex items-center gap-2 font-display text-2xl font-bold text-primary-700">
                      <i className={`${s.icon} text-xl`} aria-hidden="true"></i>
                      {s.num}
                    </dd>
                    <dt className="mt-1 text-sm text-foreground-600">{s.label}</dt>
                  </div>
                ))}
              </dl>
              <div className="mt-4 rounded-xl bg-accent-100/70 p-4">
                <p className="text-sm leading-6 text-accent-900">
                  <span className="font-semibold">一句话自白：</span>看得越多，越要慢一点；聊得越热，越要真一点。
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* principles */}
      <section id="principles" className="scroll-mt-24 border-t hairline bg-background-100/60" aria-label="内容原则">
        <div className="mx-auto max-w-6xl px-4 py-12 md:px-6 md:py-16">
          <div className="max-w-2xl">
            <p className="flex items-center gap-2 text-sm font-semibold tracking-wide text-accent-700">
              <i className="ri-heart-2-line" aria-hidden="true"></i>
              我们的坚持
            </p>
            <h2 className="mt-2 font-display text-2xl font-bold text-foreground-950 md:text-3xl">四项内容原则</h2>
            <p className="mt-3 text-[15px] leading-7 text-foreground-600">
              原则不是口号，而是每一条稿件落笔前的自检清单。它们决定了我们能做什么、不做什么。
            </p>
          </div>

          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p.title} delay={i * 80}>
                <div className="flex h-full flex-col rounded-2xl border hairline bg-background-50 p-6">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-background-100 text-2xl text-primary-600">
                    <i className={p.icon} aria-hidden="true"></i>
                  </span>
                  <h3 className="mt-4 font-display text-lg font-semibold text-foreground-950">{p.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-foreground-600">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border hairline bg-background-50 p-5">
              <h3 className="flex items-center gap-2 font-display text-base font-semibold text-foreground-900">
                <i className="ri-layout-grid-line text-primary-600" aria-hidden="true"></i>
                我们的内容栏目
              </h3>
              <ul className="mt-3 flex flex-wrap gap-2">
                {columns.map((c) => (
                  <li key={c.key}>
                    <Link to={`/rexun?cat=${encodeURIComponent(c.key)}`} className="whitespace-nowrap rounded-full bg-background-100 px-3 py-1.5 text-sm text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-700">
                      {c.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border hairline bg-background-50 p-5">
              <h3 className="flex items-center gap-2 font-display text-base font-semibold text-foreground-900">
                <i className="ri-route-line text-accent-600" aria-hidden="true"></i>
                内容生产流程
              </h3>
              <ol className="mt-3 space-y-2 text-sm text-foreground-700">
                <li className="flex items-center gap-2"><span className="flex h-5 w-5 items-center justify-center rounded-full bg-accent-500 text-[10px] text-background-50">1</span>选题与线索收集</li>
                <li className="flex items-center gap-2"><span className="flex h-5 w-5 items-center justify-center rounded-full bg-accent-500 text-[10px] text-background-50">2</span>多方信源交叉核实</li>
                <li className="flex items-center gap-2"><span className="flex h-5 w-5 items-center justify-center rounded-full bg-accent-500 text-[10px] text-background-50">3</span>克制表达与二审把关</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* word of mouth */}
      <section className="bg-background-50" aria-label="用户口碑">
        <div className="mx-auto max-w-6xl px-4 py-12 md:px-6 md:py-16">
          <div className="text-center">
            <h2 className="font-display text-2xl font-bold text-foreground-950 md:text-3xl">瓜友们怎么说</h2>
            <p className="mt-3 text-foreground-600">真实的评价，是我们持续更新的最大动力。</p>
          </div>
          <div className="mt-8 grid gap-5 md:grid-cols-3">
            {COMMENTS.map((c, i) => (
              <Reveal key={c.name} delay={i * 90}>
                <figure className="h-full rounded-2xl border hairline bg-background-100/40 p-6">
                  <div className="flex items-center gap-1 text-accent-500" aria-label="五颗星好评">
                    {[0, 1, 2, 3, 4].map((s) => (
                      <i key={s} className="ri-star-fill text-sm" aria-hidden="true"></i>
                    ))}
                  </div>
                  <blockquote className="mt-3 text-[15px] leading-7 text-foreground-700">「{c.text}」</blockquote>
                  <figcaption className="mt-4 flex items-center gap-2.5">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-500 text-background-50">{c.name.slice(0, 1)}</span>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">{c.name}</p>
                      <p className="text-xs text-foreground-500">{c.role}</p>
                    </div>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* contact / contribute */}
      <section id="contact" className="scroll-mt-24 border-t hairline bg-background-100/60" aria-label="投稿与合作">
        <div className="mx-auto grid max-w-6xl gap-10 px-4 py-12 md:px-6 md:py-16 lg:grid-cols-2">
          <Reveal>
            <p className="flex items-center gap-2 text-sm font-semibold tracking-wide text-primary-700">
              <i className="ri-send-plane-line" aria-hidden="true"></i>
              投稿与合作
            </p>
            <h2 className="mt-2 font-display text-2xl font-bold text-foreground-950 md:text-3xl">联系我们</h2>
            <p className="mt-4 text-[15px] leading-7 text-foreground-700">
              欢迎投递好剧安利、圈内线索或热点解读，也欢迎洽谈转载与内容合作。
              请务必附上可查证的信源链接，编辑部会在核验后与你沟通。我们承诺不使用、不传播未经核实或涉及隐私的内容。
            </p>

            <ul className="mt-6 space-y-3 text-sm text-foreground-700">
              <li className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-background-200 text-primary-700"><i className="ri-mail-line" aria-hidden="true"></i></span>
                guatian@example.com
              </li>
              <li className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-background-200 text-primary-700"><i className="ri-time-line" aria-hidden="true"></i></span>
                编辑部工作时间 · 工作日 09:00-18:00
              </li>
              <li className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-background-200 text-primary-700"><i className="ri-flashlight-fill" aria-hidden="true"></i></span>
                收到线索后通常在 1-3 个工作日内回复
              </li>
            </ul>

            <div className="mt-6 rounded-xl bg-accent-100/70 p-4 text-sm leading-6 text-accent-900">
              温馨提示：为保证信息真实与信息安全，请勿在表单中上传或填写未经核实、涉及他人隐私或侵权的素材。
            </div>
          </Reveal>

          <Reveal delay={100}>
            <form onSubmit={onSubmit} data-readdy-form={FORM_ID} className="rounded-2xl border hairline bg-background-50 p-6 md:p-8">
              <h3 className="font-display text-xl font-bold text-foreground-950">投稿 / 合作表单</h3>
              <p className="mt-2 text-sm text-foreground-500">带 <span className="text-primary-600">*</span> 为必填项</p>

              {status === "ok" && (
                <div className="mt-5 flex items-start gap-3 rounded-xl bg-primary-100/80 p-4 text-primary-900">
                  <i className="ri-checkbox-circle-fill text-xl" aria-hidden="true"></i>
                  <div>
                    <p className="font-semibold">感谢提交！</p>
                    <p className="mt-1 text-sm leading-6">我们已经收到你的信息，编辑部将尽快与你联系。</p>
                  </div>
                </div>
              )}
              {status === "err" && formError && (
                <div className="mt-5 flex items-start gap-3 rounded-xl bg-accent-100 p-4 text-accent-900">
                  <i className="ri-error-warning-fill text-xl" aria-hidden="true"></i>
                  <p className="text-sm leading-6">{formError}</p>
                </div>
              )}

              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <div className="flex flex-col">
                  <label htmlFor="c-name" className="text-sm font-medium text-foreground-800">您的称呼 <span className="text-primary-600">*</span></label>
                  <input id="c-name" name="name" required maxLength={30} className="mt-2 rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100" placeholder="怎么称呼你" />
                </div>
                <div className="flex flex-col">
                  <label htmlFor="c-email" className="text-sm font-medium text-foreground-800">联系邮箱 <span className="text-primary-600">*</span></label>
                  <input id="c-email" name="email" type="email" required maxLength={60} className="mt-2 rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100" placeholder="用于接收回复" />
                </div>
              </div>

              <div className="mt-4 flex flex-col">
                <label htmlFor="c-topic" className="text-sm font-medium text-foreground-800">事由类型 <span className="text-primary-600">*</span></label>
                <select id="c-topic" name="topic" required className="mt-2 rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100">
                  <option value="">请选择类型</option>
                  <option>投稿瓜料 / 线索</option>
                  <option>好剧好综推荐</option>
                  <option>转载 / 内容合作</option>
                  <option>意见反馈</option>
                  <option>其他</option>
                </select>
              </div>

              <div className="mt-4 flex flex-col">
                <label htmlFor="c-msg" className="flex items-center justify-between text-sm font-medium text-foreground-800">
                  内容描述 <span className="font-normal text-foreground-400">最多 500 字</span>
                </label>
                <textarea id="c-msg" name="message" required maxLength={500} rows={5} className="mt-2 resize-none rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100" placeholder="请简要说明来意；投稿请附上可查证的信源链接"></textarea>
              </div>

              {/* honeypot — anti-spam */}
              <div className="form-hp" aria-hidden="true">
                <input type="text" name="contact_alt" tabIndex={-1} autoComplete="off" readOnly />
              </div>

              <button type="submit" className="mt-6 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600">
                <i className="ri-send-plane-fill" aria-hidden="true"></i>
                提交投稿 / 留言
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
}